/**
 * Product Finder - Progressive Filter System with iOS-style Length Picker
 */
class ProductFinder {
  constructor() {
    this.root = document.querySelector('.product-finder');
    this.data = this.loadDataFromDom();
    this.controlProducts = this.loadSimpleProductsFromDom('.pf-control');
    this.powerProducts = this.loadSimpleProductsFromDom('.pf-power');
    this.currentStep = 1;
    this.totalSteps = 8;
    this.filters = {
      location: null,
      mounting_type: null,
      cct: null,
      lumens: null,
      waterproof_rating: null
    };
    this.filterOrder = ['location', 'mounting_type', 'cct', 'lumens', 'waterproof_rating'];
    this.selectedProducts = new Map(); // variantId -> { length, variant }
    this.selectedControl = null;
    this.selectedPower = null;
    this.isCheckingOut = false;
    this.timelineCenterStep = null;
    this.timelineResizeTimer = null;

    this.reducedMotion = false;
    try {
      this.reducedMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    } catch (_) {
      this.reducedMotion = false;
    }

    this.maxStepReached = 1;
    
    this.init();
  }

  loadSimpleProductsFromDom(selector) {
    try {
      if (!this.root) return [];

      const nodes = this.root.querySelectorAll(selector);
      return Array.from(nodes).map((el) => {
        const d = el.dataset || {};
        return {
          variantId: d.variantId ? Number(d.variantId) : null,
          productId: d.productId ? Number(d.productId) : null,
          productTitle: d.productTitle || '',
          productUrl: d.productUrl || '',
          productImage: d.productImage || '',
          variantTitle: d.variantTitle || '',
          priceCents: d.priceCents !== '' && d.priceCents != null ? Number(d.priceCents) : null,
          available: String(d.available) === 'true'
        };
      }).filter(x => x.variantId);
    } catch (e) {
      console.error(`Failed to load ${selector} data`, e);
      return [];
    }
  }

  loadDataFromDom() {
    try {
      if (!this.root) return [];

      const dataRoot = this.root.querySelector('.product-finder__data');
      const nodes = this.root.querySelectorAll('.pf-variant');

      const items = Array.from(nodes).map((el) => {
        const d = el.dataset || {};

        const priceCents = d.priceCents !== '' && d.priceCents != null ? Number(d.priceCents) : null;
        const compareAtPriceCents = d.compareAtCents !== '' && d.compareAtCents != null ? Number(d.compareAtCents) : null;

        return {
          variantId: d.variantId ? Number(d.variantId) : null,
          productId: d.productId ? Number(d.productId) : null,
          productTitle: d.productTitle || '',
          productUrl: d.productUrl || '',
          productImage: d.productImage || '',
          variantTitle: d.variantTitle || '',
          priceCents,
          compareAtPriceCents,
          available: String(d.available) === 'true',
          location: d.location || '',
          mounting_type: d.mountingType || '',
          cct: d.cct || '',
          lumens: d.lumens || '',
          waterproof_rating: d.waterproofRating || '',
          dimension: d.dimension || ''
        };
      }).filter(x => x.variantId);

      // Keep a copy on window for debugging.
      window.productFinderData = items;
      window.productFinderDebug = {
        collectionHandle: dataRoot?.dataset?.collectionHandle || '',
        collectionTitle: dataRoot?.dataset?.collectionTitle || '',
        productsCount: dataRoot?.dataset?.productsCount ? Number(dataRoot.dataset.productsCount) : null,
        items: items.length
      };

      return items;
    } catch (e) {
      console.error('Failed to load product finder data from DOM', e);
      return [];
    }
  }

  init() {
    this.bindEvents();

    if (!Array.isArray(this.data) || this.data.length === 0) {
      this.renderDataError();
      return;
    }

    this.renderStep(1);
    this.updateProgress();
    this.centerActiveTimelineStep(true);
  }

  renderDataError() {
    // Keep user on Step 1 with a friendly error.
    this.currentStep = 1;
    this.maxStepReached = 1;
    this.updateProgress();

    document.querySelectorAll('.filter-step').forEach(el => el.classList.remove('active'));
    const step1El = document.querySelector('.filter-step[data-step="1"]');
    if (step1El) {
      step1El.classList.add('active');
      const options = step1El.querySelector('.filter-options[data-filter="location"]');
      if (options) {
        options.innerHTML = `
          <div class="no-results" style="grid-column: 1 / -1;">
            <div class="no-results__icon">⚠</div>
            <p class="no-results__text">Product Finder data didn't load. Please refresh the page.</p>
          </div>
        `;
      }
    }
  }

  formatMoneyFromCents(cents) {
    if (cents == null || cents === '') return '';

    const n = Number(cents);
    if (!Number.isFinite(n)) return '';

    // Shopify theme helper if available
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        // Many themes expose money_format on Shopify object; fallback to a common format.
        const fmt = (window.Shopify.money_format || '${{amount}}');
        return window.Shopify.formatMoney(n, fmt);
      }
    } catch (_) {
      // ignore
    }

    return '$' + (n / 100).toFixed(2);
  }

  bindEvents() {
    // Back buttons
    document.querySelectorAll('.filter-back-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const backTo = parseInt(e.target.dataset.back);
        this.goToStep(backTo);
      });
    });

    // Reset button
    const resetBtn = document.querySelector('.results-reset-btn');
    if (resetBtn) {
      resetBtn.addEventListener('click', () => this.reset());
    }

    // Timeline step clicks
    document.querySelectorAll('.timeline-step').forEach(step => {
      step.addEventListener('click', (e) => {
        const stepNum = parseInt(step.dataset.step);
        if (stepNum <= this.maxStepReached) this.goToStep(stepNum);
      });
    });

    window.addEventListener('resize', () => {
      if (this.timelineResizeTimer) {
        clearTimeout(this.timelineResizeTimer);
      }

      this.timelineResizeTimer = setTimeout(() => {
        this.centerActiveTimelineStep(true);
      }, 90);
    }, { passive: true });
  }

  centerActiveTimelineStep(force = false) {
    if (!this.root) return;

    const isMobile = window.matchMedia
      ? window.matchMedia('(max-width: 900px)').matches
      : window.innerWidth <= 900;

    if (!isMobile) {
      this.timelineCenterStep = null;
      return;
    }

    if (!force && this.timelineCenterStep === this.currentStep) return;

    const timeline = this.root.querySelector('.timeline');
    if (!timeline) return;

    const activeStep = timeline.querySelector('.timeline-step.active')
      || timeline.querySelector(`.timeline-step[data-step="${this.currentStep}"]`);

    if (!activeStep) return;

    const targetLeft = activeStep.offsetLeft - ((timeline.clientWidth - activeStep.clientWidth) / 2);
    const maxScroll = Math.max(0, timeline.scrollWidth - timeline.clientWidth);
    const clamped = Math.max(0, Math.min(maxScroll, targetLeft));

    timeline.scrollTo({
      left: clamped,
      behavior: this.reducedMotion ? 'auto' : 'smooth'
    });

    this.timelineCenterStep = this.currentStep;
  }

  getSelectedStripProductCount() {
    let count = 0;
    this.selectedProducts.forEach((selection) => {
      const qty = Number(selection?.length || 0);
      if (qty > 0) count += 1;
    });
    return count;
  }

  syncStep6ContinueState() {
    const nextBtn = this.root ? this.root.querySelector('.step6-next-btn') : null;
    if (!nextBtn) return;
    const canContinue = this.getSelectedStripProductCount() > 0;
    nextBtn.disabled = !canContinue;
    nextBtn.setAttribute('aria-disabled', canContinue ? 'false' : 'true');
  }

  getFilteredData() {
    return this.data.filter(item => {
      for (const [key, value] of Object.entries(this.filters)) {
        if (value !== null && item[key] !== value) {
          return false;
        }
      }
      return true;
    });
  }

  getUniqueValues(filterKey) {
    const filtered = this.getFilteredData();
    const values = new Map();
    
    filtered.forEach(item => {
      const val = item[filterKey];
      if (val && val.trim() !== '' && val !== '/' && val !== 'N/A') {
        if (!values.has(val)) {
          values.set(val, 0);
        }
        values.set(val, values.get(val) + 1);
      }
    });

    return Array.from(values.entries())
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }

  renderStep(step) {
    // Hide all steps
    document.querySelectorAll('.filter-step').forEach(el => {
      el.classList.remove('active');
    });

    // Step 6 is special - show product cards with length picker
    if (step === 6) {
      this.renderProductSelection();
      return;
    }

    if (step === 7) {
      this.renderAccessoryStep('control');
      return;
    }

    if (step === 8) {
      this.renderAccessoryStep('power');
      return;
    }

    // Show current step
    const currentStepEl = document.querySelector(`.filter-step[data-step="${step}"]`);
    if (currentStepEl) {
      currentStepEl.classList.add('active');
    }

    // Get filter key for current step
    const filterKey = this.filterOrder[step - 1];
    const optionsContainer = document.querySelector(`.filter-options[data-filter="${filterKey}"]`);
    
    if (optionsContainer) {
      const uniqueValues = this.getUniqueValues(filterKey);
      
      if (uniqueValues.length === 0) {
        // No options available: stay on this step and let user skip.
        optionsContainer.innerHTML = `
          <div class="no-results" style="grid-column: 1 / -1;">
            <div class="no-results__icon">🔎</div>
            <p class="no-results__text">No options available for this step. You can skip it.</p>
          </div>
        `;

        const existingSkipBtns = currentStepEl.querySelectorAll('.filter-skip-btn');
        existingSkipBtns.forEach(btn => btn.remove());
        if (step < this.totalSteps) {
          const skipBtn = document.createElement('button');
          skipBtn.className = 'filter-skip-btn';
          skipBtn.textContent = 'Skip this step';
          skipBtn.addEventListener('click', () => this.goToStep(step + 1));
          currentStepEl.appendChild(skipBtn);
        }

        this.currentStep = step;
        this.maxStepReached = Math.max(this.maxStepReached, step);
        this.updateProgress();
        return;
      }

      optionsContainer.innerHTML = uniqueValues.map(([value, count]) => `
        <div class="filter-option" data-value="${this.escapeHtml(value)}">
          <span class="filter-option__label">${this.escapeHtml(value)}</span>
          <span class="filter-option__count">${count} product${count !== 1 ? 's' : ''}</span>
        </div>
      `).join('');

      // Remove any existing skip buttons
      const existingSkipBtns = currentStepEl.querySelectorAll('.filter-skip-btn');
      existingSkipBtns.forEach(btn => btn.remove());

      // Add skip button if not first step
      if (step > 1) {
        const skipBtn = document.createElement('button');
        skipBtn.className = 'filter-skip-btn';
        skipBtn.textContent = 'Skip this step';
        skipBtn.addEventListener('click', () => {
          this.goToStep(step + 1);
        });
        currentStepEl.appendChild(skipBtn);
      }

      // Bind click events
      optionsContainer.querySelectorAll('.filter-option').forEach(option => {
        option.addEventListener('click', () => {
          const value = option.dataset.value;
          this.selectOption(filterKey, value, step);
        });
      });
    }

    this.currentStep = step;
    this.maxStepReached = Math.max(this.maxStepReached, step);
    this.updateProgress();
    this.centerActiveTimelineStep();
  }

  renderProductSelection() {
    // Hide filter steps, show step 6 (product selection)
    document.querySelectorAll('.filter-step').forEach(el => {
      el.classList.remove('active');
    });

    const step6El = document.querySelector('.filter-step[data-step="6"]');
    if (step6El) {
      step6El.classList.add('active');
      
      // Update title
      const titleEl = step6El.querySelector('.filter-step__title');
      if (titleEl) {
        titleEl.textContent = 'Select your products and lengths';
      }

      // Get filtered results
      const results = this.getFilteredData();
      
      // Group by product
      const productMap = new Map();
      results.forEach(item => {
        if (!productMap.has(item.productId)) {
          productMap.set(item.productId, []);
        }
        productMap.get(item.productId).push(item);
      });

      const optionsContainer = step6El.querySelector('.filter-options[data-filter="dimension"]');
      
      if (productMap.size === 0) {
        optionsContainer.innerHTML = `
          <div class="no-results" style="grid-column: 1/-1;">
            <div class="no-results__icon">🔍</div>
            <p class="no-results__text">No products match your criteria. Try adjusting your filters.</p>
          </div>
        `;
      } else {
        optionsContainer.innerHTML = Array.from(productMap.entries()).map(([productId, variants]) => {
          const firstVariant = variants[0];
          return this.renderProductCardWithPicker(firstVariant, variants);
        }).join('');

        // Initialize all pickers
        this.initAllPickers();
      }

      // Remove any existing button containers first
      const existingBtnContainers = step6El.querySelectorAll('.step6-buttons');
      existingBtnContainers.forEach(container => container.remove());

      // Add back button and checkout button
      const btnContainer = document.createElement('div');
      btnContainer.className = 'step6-buttons';
      btnContainer.innerHTML = `
        <button class="filter-back-btn" data-back="5">← Back</button>
        <button class="checkout-all-btn step6-next-btn">Continue to Control</button>
      `;
      optionsContainer.parentNode.appendChild(btnContainer);

      // Bind back button
      btnContainer.querySelector('.filter-back-btn').addEventListener('click', () => {
        this.goToStep(5);
      });

      // Continue to Step 7
      btnContainer.querySelector('.step6-next-btn').addEventListener('click', () => {
        if (this.getSelectedStripProductCount() === 0) {
          alert('Please select at least one strip light length before continuing');
          return;
        }
        this.goToStep(7);
      });

      this.syncStep6ContinueState();
    }

    this.currentStep = 6;
    this.maxStepReached = Math.max(this.maxStepReached, 6);
    this.updateProgress();
    this.centerActiveTimelineStep();
  }

  renderProductCardWithPicker(variant, allVariants) {
    const specs = [];
    if (variant.cct) specs.push(variant.cct);
    if (variant.lumens) specs.push(variant.lumens);
    if (variant.waterproof_rating) specs.push(variant.waterproof_rating);

    const escapedTitle = this.escapeHtml(variant.productTitle || 'Product');
    const hasQuickView = Boolean(variant.productUrl && String(variant.productUrl).trim());
    const quickViewId = `ProductFinderQuickview-${variant.productId || 'p'}-${variant.variantId}`;
    const escapedProductUrl = this.escapeHtml(variant.productUrl || '');
    const escapedQuickViewLabel = this.escapeHtml(`Choose product options for ${variant.productTitle || 'this product'}`);

    // Generate length options (0-100 ft)
    const lengthOptions = [];
    for (let i = 0; i <= 100; i++) {
      lengthOptions.push(i);
    }

    const imageHtml = variant.productImage
      ? `
        <img src="${variant.productImage}" 
             alt="${escapedTitle}" 
             loading="lazy">`
      : `
        <div class="product-card-picker__image-placeholder">
          No image
        </div>`;

    const imageBlock = hasQuickView
      ? `
        <button
          type="button"
          class="product-card-picker__quickview-trigger product-card-picker__image-trigger"
          is="hover-button"
          aria-controls="${quickViewId}"
          aria-expanded="false"
          aria-label="Quick view ${escapedTitle}"
        >
          ${imageHtml}
        </button>`
      : imageHtml;

    const titleBlock = hasQuickView
      ? `
        <button
          type="button"
          class="product-card-picker__quickview-trigger product-card-picker__title-trigger"
          is="hover-button"
          aria-controls="${quickViewId}"
          aria-expanded="false"
        >
          ${escapedTitle}
        </button>`
      : escapedTitle;

    const quickViewModal = hasQuickView
      ? `
        <quick-view id="${quickViewId}" class="quick-view x-modal drawer z-40 fixed bottom-0 left-0 h-full w-full pointer-events-none" data-product-url="${escapedProductUrl}" role="dialog" aria-label="${escapedQuickViewLabel}" aria-modal="true" hidden>
          <overlay-element class="overlay fixed-modal invisible opacity-0 fixed bottom-0 left-0 w-full h-screen pointer-events-none" aria-controls="${quickViewId}" aria-expanded="false"></overlay-element>
          <div class="drawer__inner z-10 absolute top-0 flex flex-col w-full h-full overflow-hidden">
            <gesture-element class="drawer__header flex justify-between opacity-0 invisible relative">
              <button class="button button--secondary button--close drawer__close z-1 absolute top-0 right-0 flex items-center justify-center opacity-0" type="button" is="hover-button" aria-controls="${quickViewId}" aria-expanded="false" aria-label="Close">
                <span class="btn-fill" data-fill></span>
                <span class="btn-text" aria-hidden="true">&times;</span>
              </button>
            </gesture-element>
            <div class="quick-view__content drawer__content opacity-0 invisible flex flex-col h-full grow shrink"></div>
          </div>
        </quick-view>`
      : '';

    const price = this.formatMoneyFromCents(variant.priceCents);
    const compareAt = this.formatMoneyFromCents(variant.compareAtPriceCents);
    const compareHtml = compareAt && compareAt !== price ? `<span class="compare-at">${compareAt}</span>` : '';

    return `
      <div class="product-card-picker" data-variant-id="${variant.variantId}" data-product-id="${variant.productId}">
        <div class="product-card-picker__image">
          ${imageBlock}
        </div>
        <div class="product-card-picker__info">
          <h3 class="product-card-picker__title">${titleBlock}</h3>
          <div class="product-card-picker__price">${price}${compareHtml}</div>
          ${specs.length > 0 ? `
            <div class="product-card-picker__specs">
              ${specs.slice(0, 3).map(spec => `<span class="product-card-picker__spec">${this.escapeHtml(spec)}</span>`).join('')}
            </div>
          ` : ''}
        </div>
        
        <div class="length-picker-wrapper">
          <label class="length-picker-label" for="pf-wheel-${variant.variantId}">Select Length (ft)</label>
          <div class="ios-wheel" data-variant-id="${variant.variantId}">
            <div class="ios-wheel__highlight" aria-hidden="true"></div>
            <div class="ios-wheel__viewport" id="pf-wheel-${variant.variantId}" role="listbox" aria-label="Length in feet">
              ${lengthOptions.map(len => `
                <div class="ios-wheel__item" role="option" data-value="${len}">${len} ft</div>
              `).join('')}
            </div>
          </div>
          <div class="length-picker-info">
            <span class="selected-length">0 ft</span> = <span class="selected-qty">0 units</span>
          </div>
        </div>
        ${quickViewModal}
      </div>
    `;
  }

  initAllPickers() {
    document.querySelectorAll('.ios-wheel').forEach(picker => {
      this.initWheel(picker);
    });
  }

  initWheel(wheelEl) {
    const viewport = wheelEl.querySelector('.ios-wheel__viewport');
    const items = Array.from(wheelEl.querySelectorAll('.ios-wheel__item'));
    const variantId = wheelEl.dataset.variantId;
    const card = wheelEl.closest('.product-card-picker');
    if (!viewport || items.length === 0 || !card) return;

    // Measure item height from the DOM.
    const itemHeight = items[0].getBoundingClientRect().height || 44;

    const setIndex = (index, { smooth = true } = {}) => {
      const clamped = Math.max(0, Math.min(items.length - 1, index));
      const top = clamped * itemHeight;
      viewport.scrollTo({ top, behavior: smooth ? 'smooth' : 'auto' });
      this.applyWheelSelection(wheelEl, clamped);
    };

    // Initialize at 0 ft.
    const initialIndex = 0;
    viewport.scrollTop = initialIndex * itemHeight;
    this.applyWheelSelection(wheelEl, initialIndex);

    // Click-to-select.
    items.forEach((item, idx) => {
      item.addEventListener('click', () => setIndex(idx));
    });

    // Scroll handling: update active item and snap on scroll end.
    let raf = 0;
    let scrollEndTimer = 0;
    const onScroll = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const idx = Math.round(viewport.scrollTop / itemHeight);
        this.applyWheelSelection(wheelEl, idx);
      });

      if (scrollEndTimer) clearTimeout(scrollEndTimer);
      scrollEndTimer = setTimeout(() => {
        const idx = Math.round(viewport.scrollTop / itemHeight);
        setIndex(idx, { smooth: true });
      }, 120);
    };

    viewport.addEventListener('scroll', onScroll, { passive: true });
  }

  applyWheelSelection(wheelEl, index) {
    const viewport = wheelEl.querySelector('.ios-wheel__viewport');
    const items = Array.from(wheelEl.querySelectorAll('.ios-wheel__item'));
    const variantId = wheelEl.dataset.variantId;
    const card = wheelEl.closest('.product-card-picker');
    if (!viewport || items.length === 0 || !card) return;

    const clampedIndex = Math.max(0, Math.min(items.length - 1, index));
    items.forEach((item, i) => {
      const active = i === clampedIndex;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    // Add a subtle iPhone-like wheel perspective (kept lightweight).
    // Only style a small band around the active item for performance.
    if (!this.reducedMotion) {
      const maxAffect = 7;
      const start = Math.max(0, clampedIndex - maxAffect);
      const end = Math.min(items.length - 1, clampedIndex + maxAffect);

      for (let i = 0; i < items.length; i++) {
        const item = items[i];

        if (i < start || i > end) {
          item.style.transform = '';
          item.style.opacity = '';
          item.style.filter = '';
          continue;
        }

        const delta = i - clampedIndex;
        const dist = Math.abs(delta);
        const t = dist / maxAffect; // 0..1

        const opacity = 1 - (t * 0.72);
        const scale = 1 - (t * 0.10);
        const rotate = delta * 8; // degrees

        item.style.opacity = String(Math.max(0.20, Math.min(1, opacity)));
        item.style.transform = `perspective(420px) rotateX(${rotate}deg) scale(${scale})`;
        item.style.filter = '';
      }
    }

    const rawValue = parseInt(items[clampedIndex].dataset.value, 10);
    const value = Number.isFinite(rawValue) ? Math.max(0, rawValue) : 0;
    const lengthEl = card.querySelector('.selected-length');
    const qtyEl = card.querySelector('.selected-qty');
    if (lengthEl) lengthEl.textContent = `${value} ft`;
    if (qtyEl) qtyEl.textContent = `${value} unit${value > 1 ? 's' : ''}`;

    this.selectedProducts.set(variantId, {
      length: value,
      variant: this.data.find(d => String(d.variantId) === String(variantId))
    });

    this.updateProgress();
    this.syncStep6ContinueState();
  }

  renderAccessoryStep(type) {
    const isControl = type === 'control';
    const stepNum = isControl ? 7 : 8;
    const dataSet = isControl ? this.controlProducts : this.powerProducts;
    const selected = isControl ? this.selectedControl : this.selectedPower;

    document.querySelectorAll('.filter-step').forEach(el => {
      el.classList.remove('active');
    });

    const stepEl = document.querySelector(`.filter-step[data-step="${stepNum}"]`);
    if (!stepEl) return;
    stepEl.classList.add('active');

    const optionsContainer = stepEl.querySelector(`.filter-options[data-filter="${type}"]`);
    if (!optionsContainer) return;

    const optionCards = [];
    optionCards.push(`
      <button type="button" class="filter-option accessory-option${selected?.variantId === 'none' ? ' selected' : ''}" data-accessory-type="${type}" data-variant-id="none">
        <span class="accessory-option__title">Dont Need</span>
        <span class="accessory-option__price">Skip this add-on</span>
      </button>
    `);

    dataSet.forEach((item) => {
      const active = String(selected?.variantId) === String(item.variantId);
      const price = this.formatMoneyFromCents(item.priceCents);
      const unavailable = item.available === false;
      optionCards.push(`
        <button type="button" class="filter-option accessory-option${active ? ' selected' : ''}${unavailable ? ' is-disabled' : ''}" data-accessory-type="${type}" data-variant-id="${item.variantId}" ${unavailable ? 'disabled aria-disabled="true"' : ''}>
          <span class="accessory-option__title">${this.escapeHtml(item.productTitle)}</span>
          <span class="accessory-option__price">${this.escapeHtml(price || '$0.00')}${unavailable ? ' • Sold out' : ''}</span>
        </button>
      `);
    });

    optionsContainer.innerHTML = optionCards.join('');

    optionsContainer.querySelectorAll('.accessory-option').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;
        const variantId = btn.dataset.variantId;
        this.selectAccessory(type, variantId);
      });
    });

    const existingBtnContainers = stepEl.querySelectorAll('.step7-buttons, .step8-buttons');
    existingBtnContainers.forEach(container => container.remove());

    const btnContainer = document.createElement('div');
    btnContainer.className = isControl ? 'step7-buttons step6-buttons' : 'step8-buttons step6-buttons';

    if (isControl) {
      btnContainer.innerHTML = `
        <button class="filter-back-btn" data-back="6">← Back</button>
        <button class="checkout-all-btn step7-next-btn">Continue to Power</button>
      `;
      optionsContainer.parentNode.appendChild(btnContainer);

      btnContainer.querySelector('.filter-back-btn').addEventListener('click', () => {
        this.goToStep(6);
      });

      btnContainer.querySelector('.step7-next-btn').addEventListener('click', () => {
        if (!this.selectedControl) {
          alert('Please choose a control option (or select Dont Need)');
          return;
        }
        this.goToStep(8);
      });
    } else {
      btnContainer.innerHTML = `
        <button class="filter-back-btn" data-back="7">← Back</button>
      `;
      optionsContainer.parentNode.appendChild(btnContainer);

      btnContainer.querySelector('.filter-back-btn').addEventListener('click', () => {
        this.goToStep(7);
      });
    }

    this.currentStep = stepNum;
    this.maxStepReached = Math.max(this.maxStepReached, stepNum);
    this.updateProgress();
    this.centerActiveTimelineStep();
  }

  selectAccessory(type, variantId) {
    const isControl = type === 'control';
    const dataSet = isControl ? this.controlProducts : this.powerProducts;

    let selection;
    if (variantId === 'none') {
      selection = {
        variantId: 'none',
        productTitle: 'Dont Need',
        length: 0
      };
    } else {
      const found = dataSet.find(item => String(item.variantId) === String(variantId));
      if (!found) return;
      if (found.available === false) {
        alert('This option is sold out. Please choose another one or select Dont Need.');
        return;
      }
      selection = {
        variantId: found.variantId,
        productTitle: found.productTitle,
        length: 1
      };
    }

    if (isControl) {
      this.selectedControl = selection;
      this.renderAccessoryStep('control');
      return;
    }

    this.selectedPower = selection;
    this.updateProgress();
    this.renderAccessoryStep('power');

    if (this.isCheckingOut) return;

    setTimeout(() => {
      this.finalizeAndGoToCart();
    }, 120);
  }

  buildCartItems() {
    const merged = new Map();

    this.selectedProducts.forEach((selection, variantId) => {
      const id = parseInt(variantId, 10);
      const quantity = Number(selection?.length || 0);
      if (!id || quantity <= 0) return;
      merged.set(id, (merged.get(id) || 0) + quantity);
    });

    if (this.selectedControl && this.selectedControl.variantId !== 'none') {
      const controlItem = this.controlProducts.find(x => String(x.variantId) === String(this.selectedControl.variantId));
      if (!controlItem || controlItem.available !== false) {
        const id = parseInt(this.selectedControl.variantId, 10);
        if (id) merged.set(id, (merged.get(id) || 0) + 1);
      }
    }

    if (this.selectedPower && this.selectedPower.variantId !== 'none') {
      const powerItem = this.powerProducts.find(x => String(x.variantId) === String(this.selectedPower.variantId));
      if (!powerItem || powerItem.available !== false) {
        const id = parseInt(this.selectedPower.variantId, 10);
        if (id) merged.set(id, (merged.get(id) || 0) + 1);
      }
    }

    return Array.from(merged.entries()).map(([id, quantity]) => ({ id, quantity }));
  }

  async finalizeAndGoToCart() {
    const items = this.buildCartItems();

    if (items.length === 0) {
      alert('Please select at least one product before checkout');
      return;
    }

    this.isCheckingOut = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        let reason = 'Failed to add items to cart';
        try {
          const body = await response.json();
          if (body?.description) reason = body.description;
          else if (body?.message) reason = body.message;
        } catch (_) {
          // ignore parse errors
        }
        throw new Error(reason);
      }

      this.updateCartCount();
      window.location.href = '/cart';
    } catch (error) {
      console.error('Error finalizing cart:', error);
      this.isCheckingOut = false;
      alert(error?.message || 'Could not add items to cart. Please try again.');
    }
  }

  async addSingleToCart(variantId, btnEl) {
    const selection = this.selectedProducts.get(variantId);
    if (!selection) {
      alert('Please select a length first');
      return;
    }

    const btnText = btnEl.querySelector('.btn-text');
    const btnLoading = btnEl.querySelector('.btn-loading');
    
    btnEl.disabled = true;
    btnText.style.display = 'none';
    btnLoading.style.display = 'inline';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: selection.length
        })
      });

      if (response.ok) {
        btnText.textContent = 'Added!';
        btnText.style.display = 'inline';
        btnLoading.style.display = 'none';
        btnEl.classList.add('success');
        
        // Update cart count in header if exists
        this.updateCartCount();
        
        setTimeout(() => {
          btnText.textContent = 'Add to Cart';
          btnEl.classList.remove('success');
          btnEl.disabled = false;
        }, 2000);
      } else {
        throw new Error('Failed to add to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      btnText.textContent = 'Error - Try Again';
      btnText.style.display = 'inline';
      btnLoading.style.display = 'none';
      btnEl.disabled = false;
      
      setTimeout(() => {
        btnText.textContent = 'Add to Cart';
      }, 2000);
    }
  }

  async addAllToCart() {
    return this.finalizeAndGoToCart();
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Try to update common cart count selectors
      const countSelectors = [
        '.cart-count',
        '.cart-count-bubble',
        '[data-cart-count]',
        '.header__cart-count',
        '.cart-link__count'
      ];
      
      countSelectors.forEach(selector => {
        const el = document.querySelector(selector);
        if (el) {
          el.textContent = cart.item_count;
        }
      });
    } catch (e) {
      console.log('Could not update cart count');
    }
  }

  selectOption(filterKey, value, step) {
    this.filters[filterKey] = value;

    document.querySelectorAll(`.filter-options[data-filter="${filterKey}"] .filter-option`).forEach(el => {
      el.classList.remove('selected');
      if (el.dataset.value === value) {
        el.classList.add('selected');
      }
    });

    setTimeout(() => {
      this.goToStep(step + 1);
    }, 300);
  }

  goToStep(step) {
    // Clear filters for steps after the target step (only for steps 1-5)
    for (let i = step; i <= 5; i++) {
      const filterKey = this.filterOrder[i - 1];
      if (filterKey) {
        this.filters[filterKey] = null;
      }
    }

    // Reset selected products if going back
    if (step < 6) {
      this.selectedProducts.clear();
    }

    if (step < 7) {
      this.selectedControl = null;
    }

    if (step < 8) {
      this.selectedPower = null;
      this.isCheckingOut = false;
    }

    // Hide results if visible
    const resultsEl = document.querySelector('.product-finder__results');
    const stepsEl = document.querySelector('.product-finder__steps');
    if (resultsEl) resultsEl.style.display = 'none';
    if (stepsEl) stepsEl.style.display = 'block';

    this.renderStep(step);
  }

  updateProgress() {
    document.querySelectorAll('.timeline-step').forEach(stepEl => {
      const stepNum = parseInt(stepEl.dataset.step);
      stepEl.classList.remove('active', 'completed');

      if (stepNum === this.currentStep) {
        stepEl.classList.add('active');
        return;
      }

      // Step 1-5 are completed only when a value is selected.
      if (stepNum >= 1 && stepNum <= 5) {
        const key = this.filterOrder[stepNum - 1];
        if (key && this.filters[key]) stepEl.classList.add('completed');
        return;
      }

      // Step 6 considered completed if at least one product has quantity > 0.
      if (stepNum === 6 && this.getSelectedStripProductCount() > 0) {
        stepEl.classList.add('completed');
        return;
      }

      if (stepNum === 7 && this.selectedControl) {
        stepEl.classList.add('completed');
        return;
      }

      if (stepNum === 8 && this.selectedPower) {
        stepEl.classList.add('completed');
      }
    });

    // Update selected values in timeline
    this.filterOrder.forEach((filterKey) => {
      const valueEl = document.querySelector(`.timeline-step__value[data-filter="${filterKey}"]`);
      if (valueEl) {
        valueEl.textContent = this.filters[filterKey] || '';
      }
    });

    // Update dimension step value
    const dimValueEl = document.querySelector('.timeline-step__value[data-filter="dimension"]');
    if (dimValueEl) {
      const count = this.getSelectedStripProductCount();
      dimValueEl.textContent = count > 0 ? `${count} selected` : '';
    }

    const controlValueEl = document.querySelector('.timeline-step__value[data-filter="control"]');
    if (controlValueEl) {
      controlValueEl.textContent = this.selectedControl ? this.selectedControl.productTitle : '';
    }

    const powerValueEl = document.querySelector('.timeline-step__value[data-filter="power"]');
    if (powerValueEl) {
      powerValueEl.textContent = this.selectedPower ? this.selectedPower.productTitle : '';
    }
  }

  reset() {
    this.filterOrder.forEach(key => {
      this.filters[key] = null;
    });
    this.selectedProducts.clear();
    this.selectedControl = null;
    this.selectedPower = null;
    this.isCheckingOut = false;

    const resultsEl = document.querySelector('.product-finder__results');
    const stepsEl = document.querySelector('.product-finder__steps');
    if (resultsEl) resultsEl.style.display = 'none';
    if (stepsEl) stepsEl.style.display = 'block';

    this.renderStep(1);
  }

  escapeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  if (document.querySelector('.product-finder')) {
    new ProductFinder();
  }
});
